import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { calculateOffset } from 'utils/helpers';

import { apiQuery } from '../../query';
import { payoutApi, tagTypes as payoutTagTypes } from '../payout';
import {
  commissionLevelApi,
  tagTypes as commissionLevelTagTypes,
} from '../commission-level';

import {
  Employee,
  EmployeeRequest,
  OnboardingStatus,
  OnboardingInvite,
  BankChangeStatus,
  EmployeeFormValues,
  EmployeeListRequest,
  EmployeeListResponse,
  EmployeeAccountEntry,
  CreateEmployeeRequest,
  SendOnboardingRequest,
  EmployeePaymentDetails,
  OnboardingInviteOptions,
  CreateEmployeeFormValues,
  SendOnboardingInviteRequest,
} from './types';

export const tagTypes = ['Employees'];
const onboardingTag = 'Onboarding';
const bankChangeTag = 'BankChange';
const bankChangeUrl = (id: string | number, suffix = '') =>
  `${URL.EMPLOYEE_BANK_CHANGE.replace(':id', String(id))}${suffix}`;

// An employee's onboarding ("12") or an invite's ("invite:34", plan 09).
export const inviteKey = (packageId: number) => `invite:${packageId}`;
const onboardingUrl = (key: string, suffix = '') =>
  key.startsWith('invite:')
    ? `${URL.ONBOARDING_INVITE.replace(':id', key.slice(7))}${suffix}`
    : `${URL.EMPLOYEE_ONBOARDING.replace(':id', key)}${suffix}`;

export const employeesApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'employeesApi',
  tagTypes: [...tagTypes, onboardingTag, bankChangeTag],
  endpoints: (build) => ({
    getInfoAboutMe: build.query<Employee, void>({
      providesTags: tagTypes,
      query: () => ({
        method: 'get',
        url: URL.EMPLOYEE_ME,
      }),
    }),
    get: build.query<Employee, string>({
      providesTags: tagTypes,
      query: (id) => ({
        method: 'get',
        url: URL.EMPLOYEE.replace(':id', id),
      }),
    }),
    remove: build.mutation<void, string>({
      invalidatesTags: tagTypes,
      query: (id) => ({
        method: 'delete',
        url: URL.EMPLOYEE.replace(':id', id),
      }),
    }),
    // The new link is in this answer only; it is never cached.
    copyBankChangeLink: build.mutation<{ url: string }, string>({
      invalidatesTags: [bankChangeTag],
      query: (id) => ({ method: 'post', url: bankChangeUrl(id, '/link') }),
    }),
    resendBankChange: build.mutation<BankChangeStatus, string>({
      invalidatesTags: [bankChangeTag],
      query: (id) => ({ method: 'post', url: bankChangeUrl(id, '/resend') }),
    }),
    releaseBankChange: build.mutation<BankChangeStatus, string>({
      invalidatesTags: [bankChangeTag],
      query: (id) => ({ method: 'post', url: bankChangeUrl(id, '/release') }),
    }),
    cancelBankChange: build.mutation<BankChangeStatus, string>({
      invalidatesTags: [bankChangeTag, onboardingTag],
      query: (id) => ({ method: 'delete', url: bankChangeUrl(id) }),
    }),
    cancelOnboarding: build.mutation<OnboardingStatus, string>({
      invalidatesTags: [onboardingTag],
      query: (key) => ({
        method: 'delete',
        url: onboardingUrl(key),
      }),
    }),
    // The new link is in this answer only; it is never cached.
    copyOnboardingLink: build.mutation<{ url: string }, string>({
      invalidatesTags: [onboardingTag],
      query: (key) => ({
        method: 'post',
        url: onboardingUrl(key, '/link'),
      }),
    }),
    // Resend and Copy link (plan 08 §2): either one replaces the rep's link.
    resendOnboarding: build.mutation<OnboardingStatus, string>({
      invalidatesTags: [onboardingTag],
      query: (key) => ({
        method: 'post',
        url: onboardingUrl(key, '/resend'),
      }),
    }),
    retryBankChange: build.mutation<BankChangeStatus, string>({
      invalidatesTags: [bankChangeTag],
      query: (id) => ({
        method: 'post',
        url: bankChangeUrl(id, '/retry-apply'),
      }),
    }),
    listOnboardingInvites: build.query<{ rows: OnboardingInvite[] }, void>({
      keepUnusedDataFor: 0,
      providesTags: [onboardingTag],
      query: () => ({ method: 'get', url: URL.ONBOARDING_INVITES }),
    }),
    // Onboarding (plan 07 §3): never cached once the page stops using it.
    getOnboarding: build.query<OnboardingStatus, string>({
      keepUnusedDataFor: 0,
      providesTags: [onboardingTag],
      query: (key) => ({
        method: 'get',
        url: onboardingUrl(key),
      }),
    }),
    // Full bank account numbers: fetched only when Show is clicked and never
    // kept in the cache once the page stops using them.
    getPaymentDetails: build.query<EmployeePaymentDetails, string>({
      keepUnusedDataFor: 0,
      query: (id) => ({
        method: 'get',
        url: URL.EMPLOYEE_PAYMENT_DETAILS.replace(':id', id),
      }),
    }),
    // Plan 09: New employee → Send onboarding.
    getOnboardingInviteOptions: build.query<OnboardingInviteOptions, void>({
      keepUnusedDataFor: 0,
      providesTags: [onboardingTag],
      query: () => ({ method: 'get', url: URL.ONBOARDING_INVITE_OPTIONS }),
    }),
    sendOnboardingInvite: build.mutation<
      OnboardingStatus,
      SendOnboardingInviteRequest
    >({
      invalidatesTags: [onboardingTag],
      query: (data) => ({ data, method: 'post', url: URL.ONBOARDING_INVITES }),
    }),
    // A needsAttention invite: create the employee once the cause is fixed.
    createInviteEmployee: build.mutation<{ employeeId: number }, string>({
      invalidatesTags: [...tagTypes, onboardingTag],
      query: (key) => ({
        method: 'post',
        url: onboardingUrl(key, '/create-employee'),
      }),
    }),
    sendBankChange: build.mutation<BankChangeStatus, string>({
      // A bank change and an onboarding never are open together.
      invalidatesTags: [bankChangeTag, onboardingTag],
      query: (id) => ({ method: 'post', url: bankChangeUrl(id) }),
    }),
    // Plan 13: the employee's bank change (never cached once unused). A 404
    // (a Main without bank change yet) must never send the whole profile to
    // the not-found page (Dev, 26 Oct): the profile shows no bank change.
    getBankChange: build.query<BankChangeStatus, string>({
      keepUnusedDataFor: 0,
      providesTags: [bankChangeTag],
      query: (id) => ({
        method: 'get',
        url: bankChangeUrl(id),
        headers: { 'allow-not-found': 'true' },
      }),
    }),
    create: build.mutation<void, CreateEmployeeRequest>({
      invalidatesTags: tagTypes,
      query: (data) => {
        delete data.repeatPassword;

        return {
          data,
          method: 'post',
          url: URL.EMPLOYEES,
        };
      },
    }),
    // Fix contact (Documents area, owner 26 Oct): saves the email and phone
    // (an employee's sign-in email too), then resends. No phone keeps the
    // one on file.
    fixOnboardingContact: build.mutation<
      OnboardingStatus,
      { key: string; email: string; phone?: string }
    >({
      invalidatesTags: [...tagTypes, onboardingTag],
      query: ({ key, ...data }) => ({
        data,
        method: 'patch',
        url: onboardingUrl(key, '/contact'),
      }),
    }),
    reactivateEmployee: build.mutation<Employee, string>({
      // Deactivation cancels an open package; an edit can be refused
      // while one is open: read the onboarding status again too.
      invalidatesTags: [...tagTypes, onboardingTag],
      query: (id) => ({
        method: 'patch',
        url: URL.EMPLOYEE_REACTIVATE.replace(':id', id),
      }),
    }),
    deactivateEmployee: build.mutation<Employee, string>({
      // Deactivation cancels an open package; an edit can be refused
      // while one is open: read the onboarding status again too.
      invalidatesTags: [...tagTypes, onboardingTag, bankChangeTag],
      query: (id) => ({
        method: 'patch',
        url: URL.EMPLOYEE_DEACTIVATE.replace(':id', id),
      }),
    }),
    list: build.query<EmployeeListResponse, EmployeeListRequest | void>({
      providesTags: tagTypes,
      query: (params) => {
        const { page = 1, limit = 20, ...otherParams } = params || {};
        const offset = calculateOffset(page, limit);
        const searchParams = {
          limit,
          offset,
          ...otherParams,
        };

        return {
          method: 'get',
          url: addParamsToUrl(URL.EMPLOYEES, searchParams),
        };
      },
    }),
    // Send also sets the employee's commission structure (as an edit
    // would), so the employee, payouts and commission data are read again.
    sendOnboarding: build.mutation<OnboardingStatus, SendOnboardingRequest>({
      invalidatesTags: [...tagTypes, onboardingTag],
      query: ({ inviteBy, allowWire, employeeId, commissionSetId }) => ({
        method: 'post',
        data: { inviteBy, allowWire, commissionSetId },
        url: URL.EMPLOYEE_ONBOARDING.replace(':id', employeeId),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(payoutApi.util.invalidateTags(payoutTagTypes));
          dispatch(
            commissionLevelApi.util.invalidateTags(commissionLevelTagTypes)
          );
        } catch {
          // The dialog shows the server's message.
        }
      },
    }),
    edit: build.mutation<void, EmployeeRequest>({
      // Deactivation cancels an open package; an edit can be refused
      // while one is open: read the onboarding status again too.
      invalidatesTags: [...tagTypes, onboardingTag],
      query: ({ id, ...data }) => ({
        data,
        method: 'patch',
        url: URL.EMPLOYEE.replace(':id', String(id)),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(payoutApi.util.invalidateTags(payoutTagTypes));
          dispatch(
            commissionLevelApi.util.invalidateTags(commissionLevelTagTypes)
          );
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useGetQuery,
  useListQuery,
  useEditMutation,
  useCreateMutation,
  useRemoveMutation,
  useGetOnboardingQuery,
  useGetBankChangeQuery,
  useGetInfoAboutMeQuery,
  useSendBankChangeMutation,
  useSendOnboardingMutation,
  useRetryBankChangeMutation,
  useCancelBankChangeMutation,
  useResendBankChangeMutation,
  useCancelOnboardingMutation,
  useResendOnboardingMutation,
  useReleaseBankChangeMutation,
  useCopyBankChangeLinkMutation,
  useListOnboardingInvitesQuery,
  useCopyOnboardingLinkMutation,
  useLazyGetPaymentDetailsQuery,
  useDeactivateEmployeeMutation,
  useReactivateEmployeeMutation,
  useFixOnboardingContactMutation,
  useCreateInviteEmployeeMutation,
  useSendOnboardingInviteMutation,
  useGetOnboardingInviteOptionsQuery,
} = employeesApi;

export type {
  Employee,
  EmployeeRequest,
  BankChangeStatus,
  EmployeeFormValues,
  EmployeeAccountEntry,
  CreateEmployeeRequest,
  EmployeePaymentDetails,
  CreateEmployeeFormValues,
};
